import {
  Button,
  Col,
  Radio,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd/es";
import { isEmpty } from "lodash";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getQueryParams, generateQuery } from "modules/utilities/utility";
import {
  allocationStatusMapper,
  allocationStatusReasonFilters,
  filterOptions,
  internationalShippingServiceOptions,
} from "./helpers";
import ReactDatePicker from "react-datepicker";
import CsvDownload from "./CSVDownload";
import { useToast } from "modules/navbar/top/store/toasterStore";
import { useOadData, useProcessOrders } from "./store";
import CardDiv from "modules/common/card-div/CardDiv";

const { Title } = Typography;

const InternationalOrders = () => {
  const history = useNavigate();
  const [orders, setOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState({});
  const [loading, setLoading] = useState(false);
  const [tableFilter, setTableFilter] = useState({});
  const [sd, setSd] = useState("");
  const [ed, setEd] = useState("");
  const [processing, setProcessing] = useState(false);
  const [filters, setFilters] = useState({
    page: parseInt(getQueryParams("page")) || 1,
    oda_type: getQueryParams("oda_type") || null,
    allocationStatus: null,
    allocationStatusReason: null,
    orderPaymentType: null,
  });
  const [currentData, setCurrentData] = useState([]);
  const [selectAll, setSelectAll] = useState(null);
  const [shipPartner, setShipPartner] = useState(null);
  const [tablePageSize, setTablePageSize] = useState(10);
  const { oadData, fetchOadOrders } = useOadData();
  const { postProcessData } = useProcessOrders();
  const { setToaster } = useToast();


  useEffect(() => {
    setOrders(oadData);
  }, [oadData]);

  useEffect(() => {
    const filterItems = [{ ...filters }];
    history({ search: generateQuery(filterItems) });
  }, [filters, history]);

  useEffect(() => {
    if (getQueryParams("sd")) setSd(moment(getQueryParams("sd")));
    if (getQueryParams("ed")) setEd(moment(getQueryParams("ed")));
  }, []);

  const handleMarkShipped = (record, response) => {
    let _orders;
    // if record is array, then that is total orders per page
    if (Array.isArray(record)) {
      _orders = record.map((data) => {
        // All orders are updated with value at one go
        return {
          [data?.orderId]: {
            markshipped: response,
          },
        };
      });
      setSelectAll(response);
    } else {
      _orders = {
        ...selectedOrders,
        [record.orderId]: {
          markshipped: response,
        },
      };
    }
    setSelectedOrders(_orders);
  };

  const handleServiceSelect = (record, response) => {
    const _orders = {
      ...selectedOrders,
      [record.orderId]: {
        ...selectedOrders[record.orderId],
        shippingPartner: response,
      },
    };
    setSelectedOrders(_orders);
  };

  const handleTableChange = (page, filter) => {
    // When select all option is clicked and user changed the page, resetting the select All to null
    if (page.current && selectAll) {
      setSelectedOrders({});
      setSelectAll(null);
      setShipPartner(null);
    }
    const _filters = {
      ...filters,
      page: page.current,
    };
    if ("allocationStatus" in filter) {
      _filters["allocationStatus"] = filter.allocationStatus
        ? filter.allocationStatus[0]
        : null;
      setTableFilter(filter);
    }
    if ("allocationStatusReason" in filter) {
      _filters["allocationStatusReason"] = filter.allocationStatusReason
        ? filter.allocationStatusReason[0]
        : null;
      setTableFilter(filter);
    }
    if ("orderPaymentType" in filter) {
      _filters["orderPaymentType"] = filter.orderPaymentType
        ? filter.orderPaymentType[0]
        : null;
      setTableFilter(filter);
    }
    setFilters(_filters);
  };

  const handleFilterChange = (value) => {
    const _filters = {
      ...filters,
      oda_type: value,
    };
    setFilters(_filters);
  };

  const removeSelectedOrder = (orderId) => {
    if (!orderId) {
      setSelectedOrders({});
      setSelectAll(null);
      setShipPartner(null);
      return;
    }
    // if there are array of orders, below condition will allow to remove particular id from array
    if (Array.isArray(selectedOrders)) {
      let orders = selectedOrders.filter(
        (data) => Object.keys(data)[0] !== orderId
      );
      setSelectedOrders(orders);
      return;
    }
    const _orders = {
      ...selectedOrders,
    };
    delete _orders[orderId];
    setSelectedOrders(_orders);
  };

  const handleOrderClear = (orderId) => {
    const order = selectedOrders.find((data) => data[orderId]);
    if (!order) {
      return null;
    }
    return order[orderId].markshipped;
  };

  const columns = [
    {
      title: "Order Id",
      dataIndex: "order_display_id",
      key: "order_display_id",
      width: "9.5%",
      render: (value, record) => (
        <div>
          <Link className="link" to={`/lead-details/${record.caseId}`}>
            {value}
          </Link>
        </div>
      ),
    },
    {
      title: "Order Date",
      dataIndex: "createdAt",
      key: "created_at",
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (value) => moment(value).format("DD-MM-YYYY"),
    },
    {
      title: "Phone Number",
      dataIndex: "phone_number",
      key: "phone_number",
      width: "7%",
      ellipsis: true,
      render: (phone) => (
        <Tooltip placement="topLeft" title={phone}>
          {phone}
        </Tooltip>
      ),
    },
    {
      title: "City",
      dataIndex: "order_meta",
      ellipsis: true,
      width: "6.5%",
      key: "city",
      // render: value => value?.shipping_address?.city || null,
      render: (value) => (
        <Tooltip placement="topLeft" title={value?.shipping_address?.city}>
          {value?.shipping_address?.city}
        </Tooltip>
      ),
    },
    {
      title: "State",
      dataIndex: "order_meta",
      key: "province",
      width: "6.5%",
      ellipsis: true,
      render: (value) => (
        <Tooltip placement="topLeft" title={value?.shipping_address?.province}>
          {value?.shipping_address?.province}
        </Tooltip>
      ),
    },
    {
      title: "Pin Code",
      dataIndex: "order_meta",
      key: "pin_code",
      width: "8%",
      render: (value) => value?.shipping_address?.zip || null,
    },
    {
      title: "Allocation Status",
      dataIndex: "allocation_status",
      key: "allocationStatus",
      filters: [
        {
          text: "Prozo Failure",
          value: "prozoFailing",
        },
        {
          text: "Not Allocated",
          value: "not_allocated",
        },
        {
          text: "Allocated",
          value: "allocated",
        },
      ],
      width: "10%",
      filterMultiple: false,
      filteredValue: tableFilter.allocationStatus || null,
      render: (value) =>
        allocationStatusMapper[value] ? allocationStatusMapper[value] : value,
    },
    {
      title: () => {
        return <Tooltip title={"Allocation Status Reason"}>Reason</Tooltip>;
      },
      dataIndex: "allocation_status_reason",
      key: "allocationStatusReason",
      filters: [...allocationStatusReasonFilters],
      filterMultiple: false,
      filteredValue: tableFilter.allocationStatusReason || null,
      ellipsis: true,
      width: "9%",
      render: (value) => (
        <Tooltip placement="topLeft" title={value}>
          {value}
        </Tooltip>
      ),
    },
    {
      title: "Order amount",
      dataIndex: "order_meta",
      key: "order_amount",
      render: (value) => value?.total_price || null,
    },
    {
      title: "Order type",
      dataIndex: "order_type",
      key: "orderPaymentType",
      width: "7%",
      filters: [
        {
          text: "cod",
          value: "cod",
        },
        {
          text: "prepaid",
          value: "pre_paid",
        },
      ],
      filterMultiple: false,
      filteredValue: tableFilter.orderPaymentType || null,
      render: (value) => {
        if (value === "pre_paid") return "PREPAID";
        else return "COD";
      },
    },
    {
      title: "Mark Shipped",
      width: "11.7%",
      render: (_, record) => {
        return (
          <>
            <div style={{ display: "flex"}}>
              {
                // Using handleOrderClear to easily control the values
                Array.isArray(selectedOrders) ? (
                  <Radio.Group
                    onChange={(e) => handleMarkShipped(record, e.target.value)}
                    value={handleOrderClear(record.orderId)}
                  >
                    <Space direction="vertical">
                      <Radio value={true}>Yes</Radio>
                    </Space>
                  </Radio.Group>
                ) : (
                  <Radio.Group
                    onChange={(e) => handleMarkShipped(record, e.target.value)}
                    value={
                      selectedOrders[record.orderId] &&
                      selectedOrders[record.orderId].markshipped
                    }
                  >
                    <Space direction="vertical">
                      <Radio value={true}>Yes</Radio>
                    </Space>
                  </Radio.Group>
                )
              }
              <Button
                block={true}
                type="link"
                onClick={() => removeSelectedOrder(record.orderId)}
              >
                Clear
              </Button>
            </div>
          </>
        );
      },
    },
    {
      title: "Shipping Partner",
      width: "9%",
      render: (_, record) => {
        return (
          <Select
            onChange={(e) => handleServiceSelect(record, e)}
            disabled={
              !(
                selectedOrders[record.orderId] &&
                selectedOrders[record.orderId].markshipped
              )
            }
            value={
              selectedOrders[record.orderId] &&
              selectedOrders[record.orderId].shippingPartner
            }
            style={{ width: "100%" }}
            placeholder="Select"
            options={internationalShippingServiceOptions}
          />
        );
      },
    },
  ];
  useEffect(() => {
    if (filters.page === 1) {
      setCurrentData(oadData?.slice(0, tablePageSize));
    } else {
      setCurrentData(
        oadData?.slice(
          (filters.page - 1) * tablePageSize,
          filters.page * tablePageSize
        )
      );
    }
  }, [oadData]);

  const _getInternationalOrders = useCallback(
    async (sd, ed) => {
      let _sd = null;
      let _ed = null;
      setLoading(true);
      if (!(!sd || sd === "" || ed === "" || !ed)) {
        _sd = moment(sd).toISOString();
        _ed = moment(ed).toISOString();
      }
      const res = await fetchOadOrders("international", filters, _sd, _ed);
      // setting table data of the current page, data is not paginated. Getting current data or data displayed in current page of table, it will be easy to process them at a time.
      setLoading(false);
    },
    [filters]
  );

  const handleProcessOrders = async () => {
    let finalObj = {};
    if (Array.isArray(selectedOrders)) {
      for (let i = 0; i < selectedOrders.length; i++) {
        let orderId = Object.keys(selectedOrders[i])[0];
        let obj = {
          [orderId]: {
            ...selectedOrders[i][orderId],
            shippingPartner: shipPartner,
          },
        };
        // selectedOrders is array of objects, below function will convert into object of objects, as it is required to process orders
        Object.assign(finalObj, obj);
      }
    } else {
      finalObj = selectedOrders;
    }
    setProcessing(true);
    setLoading(true);
    await postProcessData(finalObj, setToaster);
    await _getInternationalOrders(sd, ed);
    setProcessing(false);
    setSelectedOrders({});
    setShipPartner(null);
    setSelectAll(null);
    setLoading(false);
    // since the current data fetched in useCallback, setting this state again
    // if we processed all order on page 15, total page count will decrease but it is not updating the table page and query params, by setting below state, fetching data once again. Can be modified if we get paginated data
    setFilters({ ...filters, page: 1 });
  };

  const handleDateFilter = async () => {
    await _getInternationalOrders(sd, ed);
  };

  useEffect(() => {
    _getInternationalOrders(sd, ed);
  }, [_getInternationalOrders, ed, sd]);

  const handlePageSizeChange = (current, size) => {
    setTablePageSize(size);
  };

  return (
    <div className="pt-4">
      <CardDiv
        title={
          <div className="d-flex justify-content-between align-items-center">
            <div style={{ display: "flex", alignItems: "center" }}>
              <h4 style={{ marginLeft: 0, whiteSpace: 'nowrap', marginRight: '10px' }}>International Orders</h4>
              <CsvDownload
                data={orders}
                extraData={selectedOrders}
                type="international"
              />
            </div>

            <div style={{ display: "flex" }}>
              <td style={{ position: "relative", zIndex: "999" }}>
                <ReactDatePicker
                  selected={sd}
                  onChange={(date) => setSd(date)}
                  formatWeekDay={(day) => day.slice(0, 3)}
                  className="form-control"
                  placeholderText="Start Date"
                />
              </td>
              <td style={{ position: "relative", zIndex: "999" }}>
                <ReactDatePicker
                  selected={ed}
                  onChange={(date) => setEd(date)}
                  formatWeekDay={(day) => day.slice(0, 3)}
                  className="form-control"
                  placeholderText="End Date"
                />
              </td>
              <Button
                type="primary"
                disabled={!sd || !ed}
                onClick={handleDateFilter}>
                Submit
              </Button>
            </div>
            <Title level={5} style={{ margin: 0, whiteSpace: 'nowrap' }}>
              Select All
            </Title>
            <Radio.Group
              onChange={(e) => handleMarkShipped(currentData, e.target.value)}
              value={selectAll}
              optionType="button"
              buttonStyle="solid"
              options={[
                {
                  label: "yes",
                  value: true,
                },
              ]}
            />
            <Button type="link" onClick={() => removeSelectedOrder()}>
              Clear
            </Button>
            <div>
              <Select
                onChange={(e) => setShipPartner(e)}
                style={{ width: "100%" }}
                placeholder="Select"
                disabled={!selectAll}
                value={shipPartner}
                allowClear
                options={internationalShippingServiceOptions}
              />
            </div>
          </div>
        }
        component={
          <div style={{ minHeight: "68vh", maxHeight: "72vh", zIndex: "-1" }}>
            <div
              className="shadow table-row rounded-bottom scrollable-container"
              style={{
                fontSize: 13.2,
              }}
            >
              <div className="rounded-bottom">
                <Table
                  columns={columns}
                  onChange={(page, filter, sort) =>
                    handleTableChange(page, filter, sort)
                  }
                  loading={loading}
                  dataSource={orders || []}
                  pagination={{
                    current: filters?.page || 1,
                    total: orders?.length,
                    onShowSizeChange: handlePageSizeChange,
                    showSizeChanger: true,
                  }}
                  style={{ width: "100%", zIndex: "0" }}
                  scroll={{ y: 380 }}
                />
              </div>
            </div>
            <div className="mt-3">
              <Row justify="end">
                <Button
                  type="primary"
                  onClick={handleProcessOrders}
                  disabled={isEmpty(selectedOrders) || processing}
                >
                  Process Orders
                </Button>
              </Row>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default InternationalOrders;
